import React, { ReactNode, useEffect, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DataTableContainer, Table, cn } from '@sib/ui';
import { useElementSize } from '@sib/ui/hooks';
import { DEFAULT_PAGE_SIZE } from '../constants';
import {
  useColumnResizeVariables,
  useGetSelectedRowOriginalData,
  useTableSettings,
  useColumnReordering,
} from '../hooks';
import { getRowId, isRowWithId } from '../utils';
import {
  MemoizedDataTableBody,
  DataTableNoResultsRow,
  DataTableNoResultsRowProps,
} from './body';
import {
  DataTableFooter,
  DataTablePagination,
  DataTablePaginationProps,
} from './footer';
import {
  DataTableDropIndicator,
  DataTableHeader,
  DataTableSettings,
} from './header';

export interface DataTableProps<T> {
  rowCount?: number;
  data: T[];
  columns: ColumnDef<T, unknown>[];
  settings: ReturnType<typeof useTableSettings<T>>;
  minPage?: number;
  loading?: boolean;
  withFixedColumnWidth?: boolean;
  withSettingsRow?: boolean;
  preserveSelectedValues?: boolean;
  withDelayedLoading?: boolean;
  hidePagination?: boolean;
  hideFooter?: boolean;
  noResultsPlaceholder?: ReactNode;
  NoResultsContainerProps?: Partial<DataTableNoResultsRowProps>;
  TableContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  TablePaginationProps?: Partial<DataTablePaginationProps>;
  TableProps?: React.HTMLAttributes<HTMLTableElement>;
  withFilters?: boolean;
  onRowClick?: (row: T) => void;
}

type WithSubRows<T> = T & { subRows?: T[] };

export const DataTable = <T,>({
  settings: {
    tableProps,
    pagination,
    onPaginationChange,
    minPage,
    originalRowSelection,
    onOriginalRowSelectionChange,
    disabledColumns,
    enableColumnReordering = false,
    exportAction,
    isExportPending,
    uploadAction,
  },
  rowCount,
  data,
  columns,
  noResultsPlaceholder,
  hideFooter = true,
  hidePagination = false,
  withFixedColumnWidth = false,
  loading = false,
  withSettingsRow = false,
  preserveSelectedValues = false,
  withDelayedLoading = false,
  NoResultsContainerProps,
  TableProps,
  TableContainerProps,
  TablePaginationProps,
  withFilters,
  onRowClick,
}: DataTableProps<T>) => {
  const showNoResults = !loading && data.length === 0;
  const { width, ref } = useElementSize<HTMLTableElement>({
    enabled: showNoResults || (loading && withDelayedLoading),
  });

  const getSelectedRowOriginalData = useGetSelectedRowOriginalData({ data });
  const table = useReactTable({
    data,
    columns,
    rowCount,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableExpanding: true,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: T) => (row as WithSubRows<T>).subRows ?? [],
    manualFiltering: true,
    ...tableProps,
    onRowSelectionChange: updater => {
      tableProps?.onRowSelectionChange?.(updater);
      if (!preserveSelectedValues) return;

      const valuesToCompare =
        typeof updater === 'function'
          ? updater(tableProps.state?.rowSelection || {})
          : updater;

      const originalData = getSelectedRowOriginalData(
        updater,
        valuesToCompare,
        originalRowSelection
      );

      onOriginalRowSelectionChange?.(originalData);
    },
  });

  const rowSelectionSnapshot = JSON.stringify(
    table.getState().rowSelection ?? {}
  );

  const {
    sensors,
    modifiers,
    reorderableColumnIds,
    handleColumnDragEnd,
    leftPinnedColumnsWidth,
    rightPinnedColumnsWidth,
  } = useColumnReordering({ table, enableColumnReordering, disabledColumns });

  useEffect(() => {
    if (!preserveSelectedValues || !onOriginalRowSelectionChange) return;

    const selection = table.getState().rowSelection;
    const hasSelection = Object.values(selection).some(Boolean);

    if (!hasSelection && originalRowSelection.length === 0) return;

    const synced = getSelectedRowOriginalData(
      () => selection,
      selection,
      originalRowSelection
    );

    const ids = (rows: T[]) =>
      rows
        .filter(isRowWithId)
        .map(row => getRowId(row))
        .sort()
        .join('\0');

    if (ids(synced) === ids(originalRowSelection)) return;

    onOriginalRowSelectionChange(synced);
  }, [
    data,
    getSelectedRowOriginalData,
    onOriginalRowSelectionChange,
    originalRowSelection,
    preserveSelectedValues,
    rowSelectionSnapshot,
    table,
  ]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const columnSizeVars = useColumnResizeVariables({ table });

  const tableState = table.getState();

  const {
    className: tableClassName,
    style: tableStyle,
    ...restTableProps
  } = TableProps ?? {};

  const tableContent = (
    <Table
      {...restTableProps}
      className={cn(
        'min-w-full border-collapse',
        !withFixedColumnWidth && 'w-max',
        tableClassName
      )}
      style={{
        ...(withFixedColumnWidth && {
          tableLayout: 'fixed',
          width: table.getTotalSize(),
          minWidth: '100%',
        }),
        ...tableStyle,
        ...columnSizeVars,
      }}
      disableWrapper
    >
      <DataTableHeader
        table={table}
        headerGroups={table.getHeaderGroups()}
        hideRounding={withSettingsRow}
        withFilters={withFilters}
        withFixedColumnWidth={withFixedColumnWidth}
        reorderableColumnIds={
          enableColumnReordering ? reorderableColumnIds : undefined
        }
      />
      <MemoizedDataTableBody
        TableDelayedLoadingIndicatorProps={{
          width,
        }}
        withDelayedLoading={withDelayedLoading}
        loading={loading}
        isEmpty={showNoResults}
        table={table}
        pageSize={pagination.pageSize || DEFAULT_PAGE_SIZE}
        rowSelection={tableState.rowSelection}
        columnPinning={tableState.columnPinning}
        onRowClick={onRowClick}
        withFixedColumnWidth={withFixedColumnWidth}
        emptyPlaceholder={
          <DataTableNoResultsRow
            colSpan={table.getAllColumns().length}
            {...NoResultsContainerProps}
            width={width}
          >
            {noResultsPlaceholder}
          </DataTableNoResultsRow>
        }
      />
      {!hideFooter && <DataTableFooter table={table} />}
    </Table>
  );

  return (
    <DataTableContainer {...TableContainerProps} ref={ref}>
      {withSettingsRow && (
        <DataTableSettings
          table={table}
          disabledColumns={disabledColumns}
          exportAction={exportAction}
          isExportPending={isExportPending}
          uploadAction={uploadAction}
        />
      )}
      <div
        className={cn(
          'shadow-subtle flex flex-1 flex-col overflow-hidden rounded-tr-md border-x border-b',
          withSettingsRow ? 'rounded-tl-0' : 'rounded-tl-md'
        )}
      >
        <div ref={scrollContainerRef} className="relative flex-1 overflow-auto">
          {enableColumnReordering ? (
            <DndContext
              sensors={sensors}
              modifiers={modifiers}
              collisionDetection={closestCenter}
              onDragEnd={handleColumnDragEnd}
              autoScroll={{ threshold: { x: 0.2, y: 0 } }}
            >
              <SortableContext
                items={reorderableColumnIds}
                strategy={horizontalListSortingStrategy}
              >
                <DataTableDropIndicator
                  containerRef={scrollContainerRef}
                  items={reorderableColumnIds}
                  offsetLeft={leftPinnedColumnsWidth}
                  offsetRight={rightPinnedColumnsWidth}
                />
                {tableContent}
              </SortableContext>
            </DndContext>
          ) : (
            tableContent
          )}
        </div>
        {data.length > 0 && typeof rowCount === 'number' && !hidePagination && (
          <DataTablePagination
            onChange={onPaginationChange}
            rowCount={rowCount}
            state={pagination}
            minPage={minPage}
            {...TablePaginationProps}
          />
        )}
      </div>
    </DataTableContainer>
  );
};
